import type {
  AdminReportDetailDto,
  AdminReportStatus,
  ListAdminReportsResultDto,
} from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin reports surface — READ-ONLY as of
 * AUT-265. The whole remaining backend surface is two GETs:
 *
 *   - `GET /admin/reports`      (cursor-paginated)
 *   - `GET /admin/reports/:id`
 *
 * **The three moderation POSTs are gone.** `POST /admin/reports/:id/approve`,
 * `/reject` and `/request-revision` were deleted server side with
 * `ReportsService.approve` / `.reject` / `.requestRevision`; they 404 now.
 * They were removed from this module rather than left dormant, because the
 * admin package imports nothing from `packages/api` — a stale mutation here
 * typechecks green and only fails in a real browser.
 *
 * The reads survive on purpose: an adjudicator cannot resolve an appeal
 * without reading the report it is about.
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list   → `['admin', 'reports', 'list', filters]`
 *   detail → `['admin', 'reports', 'detail', id]`
 *
 * 🟠 #4 `staleTime: 4 * 60_000` on the detail query — the server mints
 *      1h-TTL S3 signed URLs for media; a 4-min staleTime means the SPA
 *      proactively re-fetches WELL before any URL expiry, so the
 *      lightbox never lands on a 404. Plus the lightbox `onError`
 *      invalidates the detail query as a belt-and-braces.
 *
 * Server state lives in TanStack; the auth/session permission set lives
 * in MobX. The two never duplicate a datum.
 */

const REPORTS_BASE = '/admin/reports';

/** Stable query-key prefix for both reads. */
export const ADMIN_REPORTS_KEY = ['admin', 'reports'] as const;

/**
 * Detail-query staleTime — architect 🟠 #4. The server mints 1h-TTL
 * signed URLs; 4 minutes ensures a proactive re-fetch well before any
 * URL expires so the lightbox never 404s on an expired URL.
 */
const DETAIL_STALE_TIME_MS = 4 * 60_000;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminReportsQuerySchema`:
 *   - `status` — multi-select (CSV on the wire);
 *   - `inspectorId` — exact-match UUID;
 *   - `dateFrom` / `dateTo` — ISO date strings bounding `submittedAt`;
 *   - `q` — free-text ≥3 chars (enforced client-side before it reaches
 *           here);
 *   - `cursor` / `limit` — cursor pagination over `submittedAt DESC`.
 */
export interface AdminReportsListParams {
  status?: AdminReportStatus[];
  inspectorId?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminReportsListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.status && params.status.length > 0) query.status = params.status.join(',');
  if (params.inspectorId && params.inspectorId.length > 0) query.inspectorId = params.inspectorId;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.q && params.q.length > 0) query.q = params.q;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminReportsList = (
  params: AdminReportsListParams,
): UseQueryResult<ListAdminReportsResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_REPORTS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminReportsResultDto>(REPORTS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminReportDetail = (
  id: string | null,
): UseQueryResult<AdminReportDetailDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_REPORTS_KEY, 'detail', id],
    enabled: id !== null,
    // architect 🟠 #4 — proactive re-fetch before 1h signed-URL TTLs.
    staleTime: DETAIL_STALE_TIME_MS,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminReportDetailDto>(`${REPORTS_BASE}/${id}`, {
        signal,
      });
      return data;
    },
  });

/** Cache-helper for the lightbox `onError` URL-expiry recovery (🟠 #4). */
export const useAdminReportDetailCache = (): {
  invalidateDetail: (id: string) => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    invalidateDetail: (id: string) =>
      queryClient.invalidateQueries({ queryKey: [...ADMIN_REPORTS_KEY, 'detail', id] }),
  };
};
